import { useEffect, useRef, useState } from "react";
import { FILTER_KEYS, type FilterKey, type TicketFilter } from "@server/reporting/metrics";
import { Button } from "../../components/ui";
import { FILTER_LABELS, isFiltered, TRIAGE_FLAG_LABELS } from "./filters";

const MAX_SHOWN = 150;

// A dropdown of checkboxes with a search box, for lists as long as the company names.
function MultiSelect({ label, options, selected, onChange }: { label: string; options: readonly string[]; selected: readonly string[]; onChange: (values: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const close = (event: MouseEvent): void => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const matches = options.filter((option) => option.toLowerCase().includes(search.trim().toLowerCase()));
  const toggle = (option: string): void => onChange(selected.includes(option) ? selected.filter((value) => value !== option) : [...selected, option]);

  return (
    <div className="ms" ref={root}>
      <button type="button" className={`ms-button${selected.length > 0 ? " active" : ""}`} aria-expanded={open} onClick={() => setOpen(!open)}>
        {label}
        {selected.length > 0 && <span className="ms-count">{selected.length}</span>}
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="ms-pop">
          <input type="search" placeholder={`Search ${label.toLowerCase()}`} value={search} onChange={(event) => setSearch(event.target.value)} aria-label={`Search ${label}`} />
          <div className="ms-actions">
            <button type="button" className="link-button" onClick={() => onChange([])} disabled={selected.length === 0}>
              Clear
            </button>
            <span className="record-note">{matches.length} options</span>
          </div>
          <ul className="ms-list">
            {matches.slice(0, MAX_SHOWN).map((option) => (
              <li key={option}>
                <label>
                  <input type="checkbox" checked={selected.includes(option)} onChange={() => toggle(option)} /> {option}
                </label>
              </li>
            ))}
          </ul>
          {matches.length > MAX_SHOWN && <p className="record-note">Showing the first {MAX_SHOWN}. Search to narrow the list.</p>}
        </div>
      )}
    </div>
  );
}

// The filters every report tab shares. `options` are the values found in all tickets, so a choice never disappears when another is applied.
export function FilterBar({ filter, options, onChange }: { filter: TicketFilter; options: Record<FilterKey, string[]>; onChange: (filter: TicketFilter) => void }) {
  return (
    <div className="filter-bar" role="group" aria-label="Filters">
      <label className="filter-date">
        <span>From</span>
        <input type="date" value={filter.from ?? ""} max={filter.to} onChange={(event) => onChange({ ...filter, from: event.target.value || undefined })} />
      </label>
      <label className="filter-date">
        <span>To</span>
        <input type="date" value={filter.to ?? ""} min={filter.from} onChange={(event) => onChange({ ...filter, to: event.target.value || undefined })} />
      </label>
      {FILTER_KEYS.map((key) => (
        <MultiSelect
          key={key}
          label={FILTER_LABELS[key]}
          options={options[key]}
          selected={filter.values[key] ?? []}
          onChange={(values) => onChange({ ...filter, values: { ...filter.values, [key]: values } })}
        />
      ))}
      {filter.triage && (
        <span className="filter-flag">
          {TRIAGE_FLAG_LABELS[filter.triage]}
          <button type="button" aria-label="Remove this filter" onClick={() => onChange({ ...filter, triage: undefined })}>
            ×
          </button>
        </span>
      )}
      {isFiltered(filter) && (
        <Button variant="secondary" className="btn-sm" onClick={() => onChange({ values: {} })}>
          Reset
        </Button>
      )}
    </div>
  );
}
